import { Input, InputNumber, type InputProps, Space } from 'antd';
import { useField } from 'formik';
import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { onNumericKeyPress } from '../../utilities/form';
import FormItem, { type FormItemProps } from './FormItem';

const StyledSpace = styled(Space)`
    width: 100%;

    & .ant-input {
        border-top: none;
        border-left: none;
        border-right: none;
        box-shadow: none;
        font-size: var(--input-font-size, 16px);
        margin-left: 1rem;
        background-color: transparent;
    }

    & .ant-input-number-group-addon {
        background-color: transparent;
        border-top: none;
        border-left: none;
        border-right: none;
        font-size: var(--input-font-size, 16px);
    }

    & .ant-input-number-wrapper {
        height: 100%;
    }

    & .ant-input-number {
        border-top: none;
        border-left: none;
        border-right: none;
        box-shadow: none;
        font-size: var(--input-font-size, 16px);
        height: 100%;
        background-color: transparent;

        & input {
            padding-top: 4px;
        }
    }

    & .ant-input-group.ant-input-group-compact {
        display: flex;

        & > *:last-child {
            border-radius: 0;
        }
    }
`;

export interface InputFieldProps extends Omit<InputProps, 'value' | 'onChange' | 'autoComplete'> {
    name: string;
    label?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    showTelSymbol?: boolean;
    allowSpecialCharacters?: boolean;
}

const PhoneAndPrefixField = ({
    name,
    required,
    label,
    itemProps,
    disabled,
    showTelSymbol = true,
    allowSpecialCharacters = false,
    ...props
}: InputFieldProps) => {
    // Following this guide from Mozilla about autocomplete
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#values
    // phone will be separated for prefix and value
    // to implement this the cost is, using input hidden field for the value
    // that way, we still use the same data name for our system
    // while telling the browser when user have address autocomplete saved on their end
    // to prefill it to the correct field

    // prefix field handler
    const [prefixField, prefixMeta, { setValue: setPrefixValue }] = useField({ name: `${name}.prefix` });
    // phone field handler
    const [valueField, valueMeta, { setValue }] = useField({ name: `${name}.value` });

    const meta = useMemo(
        () => ({
            error: prefixMeta.error || valueMeta.error,
            touched: (prefixMeta.touched && valueMeta.touched) || (prefixField.value && valueMeta.touched),
            initialValue: valueMeta.initialValue,
            value: valueMeta.value,
        }),
        [
            prefixMeta.error,
            prefixMeta.touched,
            valueMeta.error,
            valueMeta.touched,
            valueMeta.initialValue,
            valueMeta.value,
            prefixField.value,
        ]
    );

    return (
        <StyledSpace direction="vertical" size="middle">
            <FormItem {...itemProps} label={label} meta={meta} required={required}>
                <Input.Group compact>
                    <input name={`${name}.prefix`} type="hidden" value={prefixField.value} />
                    <input name={`${name}.value`} type="hidden" value={valueField.value} />
                    <InputNumber
                        {...prefixField}
                        addonBefore={showTelSymbol ? '+' : ''}
                        autoComplete="off"
                        controls={false}
                        // field properties
                        disabled={disabled}
                        // override the phone number country code name
                        name="tel-country-code"
                        onChange={value => setPrefixValue(value)}
                        onKeyPress={onNumericKeyPress}
                        // fix the size for this input
                        style={{ width: '100px' }}
                        type="number"
                    />
                    <Input
                        autoComplete="off"
                        disabled={disabled}
                        // spread props
                        {...props}
                        // field properties
                        // override the phone number national name
                        name="tel-national"
                        // fix the size for this input
                        style={{ ...props.style, width: 'calc(100% - 100px)' }}
                        type="tel"
                        {...valueField}
                        onBlur={props.onBlur || valueField.onBlur}
                        onChange={event => {
                            const newValue = event.target.value;
                            setValue(allowSpecialCharacters ? newValue : newValue.replace(/[\D-]/g, ''));
                        }}
                    />
                </Input.Group>
            </FormItem>
        </StyledSpace>
    );
};

export default memo(PhoneAndPrefixField);
